import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from './useUser'
import { useLanguage } from './useLanguage'
import { useConfirmContext } from '@/store/contexts/ConfirmContext'
import { t } from '@/utils/i18n'

/**
 * PRO 업그레이드 안내를 위한 공통 훅
 * NORMAL 사용자가 제한에 걸렸을 때 PRO 업그레이드를 안내하는 확인 다이얼로그를 표시합니다.
 */
export function useProUpgrade() {
  const navigate = useNavigate()
  const { currentUser } = useUser()
  const { language } = useLanguage()
  const { confirm } = useConfirmContext()

  // 사용자 등급 확인
  const isPro = currentUser?.role === 'PRO' || currentUser?.role === 'ADMIN'
  const isNormal = currentUser?.role === 'NORMAL'

  /**
   * NORMAL 사용자인지 확인하고, PRO 업그레이드 안내 다이얼로그를 표시합니다.
   * @param message - 표시할 메시지 (기본값: "PRO로 업그레이드하시겠습니까?")
   * @returns Promise<boolean> - 확인을 누르면 true, 취소를 누르면 false
   */
  const showProUpgradeDialog = useCallback(
    async (message?: string): Promise<boolean> => {
      const defaultTitle = language === 'ko' ? '꿈이 많아지셨나요?' : 'Have more dreams?'
      const defaultDescription =
        language === 'ko'
          ? '잠시 광고 보고 제한없는 플랜으로 변경해보세요!'
          : 'Watch a short ad and switch to an unlimited plan!'

      // 목표/카테고리/계획/꿈/아이템 등 모든 제한 안내에서 동일한 업그레이드 모달 스타일 사용 (title 있으면 아이콘+타이틀 표시)
      const confirmed = await confirm(
        message != null && message.trim() !== ''
          ? {
              title: defaultTitle,
              message,
              variant: 'primary',
              cancelText: t('common.later', language),
              confirmText: t('common.upgrade', language),
            }
          : {
              title: defaultTitle,
              message: defaultDescription,
              variant: 'primary',
              cancelText: t('common.later', language),
              confirmText: t('common.upgrade', language),
            }
      )

      if (confirmed) {
        navigate('/user#user-points')
      }

      return confirmed
    },
    [confirm, navigate, language]
  )

  /**
   * 에러가 권한/생성 제한( errorCode: 'CREATION_LIMIT_EXCEEDED' )인지 확인하고 PRO 업그레이드 안내를 표시합니다.
   * 목표/계획/꿈/아이템/서브카테고리 등 모든 갯수 초과 시 동일 문구로 표시합니다.
   * @param error - API 에러 객체 (response.data.errorCode 사용)
   * @returns Promise<boolean> - 제한 에러인 경우 true, 아니면 false
   */
  const handleRestrictionError = useCallback(
    async (error: any): Promise<boolean> => {
      const errorCode = error?.response?.data?.errorCode

      if (errorCode !== 'CREATION_LIMIT_EXCEEDED') {
        return false
      }

      await new Promise((resolve) => setTimeout(resolve, 100))
      // 목표/계획/꿈/아이템/서브카테고리 등 모든 갯수 초과 시 훅 기본 title+description 사용 (중복 문구 방지)
      await showProUpgradeDialog()
      return true
    },
    [showProUpgradeDialog]
  )

  /**
   * NORMAL 사용자인지 확인하고, PRO 업그레이드 안내를 표시합니다.
   * @param message - 표시할 메시지 (선택사항)
   * @returns Promise<boolean> - PRO 사용자이면 true, NORMAL 사용자이고 확인을 누르면 true, 취소를 누르면 false
   */
  const checkAndShowUpgrade = useCallback(
    async (message?: string): Promise<boolean> => {
      // PRO 사용자는 제한 없음
      if (isPro) {
        return true
      }

      // NORMAL 사용자인 경우 업그레이드 안내
      if (isNormal) {
        return await showProUpgradeDialog(message)
      }

      // 권한 정보가 없는 경우도 업그레이드 안내
      return await showProUpgradeDialog(message)
    },
    [isPro, isNormal, showProUpgradeDialog]
  )

  return {
    isPro,
    isNormal,
    showProUpgradeDialog,
    handleRestrictionError,
    checkAndShowUpgrade,
  }
}

